import { useState, type ReactNode } from "react";
import { createId, type DataSource, type FieldSchema, type InspectorField } from "@broadpaper/core";
import { Button, Field, Segmented, Select, Switch, TextInput, NumberInput } from "../../ui/primitives.js";
import { Icon } from "../../ui/icons.js";
import { BindingField, useTokenText } from "./BindingField.js";
import { FORMAT_SUGGESTIONS } from "../DataPicker.js";

/* ------------------------------- generic list ------------------------------ */

export function ListEditor<T extends { id?: string }>({ items, onChange, renderItem, label, itemLabel = "Item", create, summary }: { items: T[]; onChange: (items: T[]) => void; renderItem: (item: T, update: (patch: Partial<T>) => void, index: number) => ReactNode; label?: string; itemLabel?: string; create: () => T; summary?: (item: T, index: number) => string }) {
  const [open, setOpen] = useState<number | null>(null);
  const update = (i: number, patch: Partial<T>) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const noun = itemLabel.toLowerCase();
  const name = (item: T, i: number) => summary?.(item, i) || `${itemLabel} ${i + 1}`;
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
    setOpen(j);
  };
  return (
    <div className="bpe-list-editor">
      {items.map((it, i) => (
        <div key={it.id ?? i} className="bpe-list-editor__item">
          <div className="bpe-list-editor__head">
            {/* Named after the item, because "Move up" appears in the block
                toolbar, the layer tree and every list editor on the page. */}
            <button type="button" className="bpe-layer__toggle" aria-expanded={open === i} aria-label={`${open === i ? "Collapse" : "Expand"} ${name(it, i)}`} onClick={() => setOpen(open === i ? null : i)}>
              <Icon name="chevron-down" />
            </button>
            <span className="bpe-truncate" title={summary?.(it, i)}>{name(it, i)}</span>
            <Button size="sm" variant="ghost" icon="arrow-up" tooltip={`Move ${noun} up`} disabled={i === 0} onClick={() => move(i, -1)} />
            <Button size="sm" variant="ghost" icon="arrow-down" tooltip={`Move ${noun} down`} disabled={i === items.length - 1} onClick={() => move(i, 1)} />
            <Button size="sm" variant="ghost" icon="trash" tooltip={`Remove ${noun}`} onClick={() => onChange(items.filter((_, j) => j !== i))} />
          </div>
          {open === i ? <div className="bpe-list-editor__body">{renderItem(it, (patch) => update(i, patch), i)}</div> : null}
        </div>
      ))}
      <Button size="sm" icon="plus" onClick={() => { onChange([...items, create()]); setOpen(items.length); }}>
        Add {label ?? itemLabel.toLowerCase()}
      </Button>
    </div>
  );
}

/* --------------------------------- columns --------------------------------- */

export interface ColumnItem {
  id: string;
  header: string;
  value: string;
  align?: "left" | "center" | "right";
  width?: number | null;
  bold?: boolean;
  total?: "none" | "sum" | "avg" | "count" | "min" | "max";
  totalFormat?: string;
  wrap?: boolean;
}

/**
 * How a column total can be shown. Money and numbers only: a total is a number,
 * whatever the column it came from holds.
 */
const TOTAL_FORMATS = [...(FORMAT_SUGGESTIONS.currency ?? []), ...(FORMAT_SUGGESTIONS.number ?? []), ...(FORMAT_SUGGESTIONS.percent ?? [])];

export function ColumnsEditor({ value, onChange, dataSources, scopeVars }: { value: ColumnItem[]; onChange: (v: ColumnItem[]) => void; dataSources: DataSource[]; scopeVars?: Record<string, FieldSchema> }) {
  return (
    <ListEditor<ColumnItem>
      items={value}
      onChange={onChange}
      itemLabel="Column"
      summary={(c) => c.header || c.value || "Column"}
      create={() => ({ id: createId("c"), header: "New column", value: "", align: "left", width: null })}
      renderItem={(c, update) => (
        <>
          <Field label="Heading" stacked><TextInput value={c.header} onChange={(header) => update({ header })} lazy /></Field>
          <Field label="Value" stacked><BindingField value={c.value} onChange={(v) => update({ value: v })} dataSources={dataSources} scopeVars={scopeVars} placeholder="Field per row" /></Field>
          <Field label="Align"><Select value={c.align ?? "left"} onChange={(v) => update({ align: v as ColumnItem["align"] })} options={[{ value: "left", label: "Left" }, { value: "center", label: "Centre" }, { value: "right", label: "Right" }]} /></Field>
          <Field label="Width"><NumberInput value={c.width ?? null} onChange={(w) => update({ width: w })} min={3} max={100} unit="%" placeholder="auto" /></Field>
          <Field label="Total"><Select value={c.total ?? "none"} onChange={(v) => update({ total: v as ColumnItem["total"] })} options={[{ value: "none", label: "None" }, { value: "sum", label: "Sum" }, { value: "avg", label: "Average" }, { value: "count", label: "Count" }, { value: "min", label: "Minimum" }, { value: "max", label: "Maximum" }]} /></Field>
          {/*
            The total's format, chosen rather than typed.
            
            It was a monospace box whose placeholder was `currency:"GBP":0`, so a
            business user who set a column total was then required to write pipe
            syntax to make it read as money — the one place in the designer where
            using an ordinary feature *demanded* an expression. The choices are the
            same list the value picker offers, so the two cannot drift.
          */}
          {c.total && c.total !== "none" ? (
            <Field label="Total shown as">
              <Select
                value={c.totalFormat ?? ""}
                onChange={(totalFormat) => update({ totalFormat: totalFormat || undefined })}
                options={[{ value: "", label: "As it comes" }, ...TOTAL_FORMATS.map((f) => ({ value: f.pipe, label: f.label }))]}
              />
            </Field>
          ) : null}
          <Field label="Bold"><Switch checked={!!c.bold} onChange={(bold) => update({ bold })} /></Field>
          <Field label="Wrap text"><Switch checked={c.wrap !== false} onChange={(wrap) => update({ wrap })} /></Field>
        </>
      )}
    />
  );
}

/* ---------------------------------- series --------------------------------- */

export interface SeriesItem {
  id: string;
  label: string;
  value: string;
  color?: string;
}

export function SeriesEditor({ value, onChange, dataSources, scopeVars, renderColor }: { value: SeriesItem[]; onChange: (v: SeriesItem[]) => void; dataSources: DataSource[]; scopeVars?: Record<string, FieldSchema>; renderColor: (value: string | undefined, onChange: (v: string | undefined) => void) => ReactNode }) {
  return (
    <ListEditor<SeriesItem>
      items={value}
      onChange={onChange}
      itemLabel="Series"
      summary={(s) => s.label || s.value || "Series"}
      create={() => ({ id: createId("s"), label: `Series ${value.length + 1}`, value: "" })}
      renderItem={(s, update) => (
        <>
          <Field label="Label"><TextInput value={s.label} onChange={(label) => update({ label })} lazy /></Field>
          <Field label="Value" stacked><BindingField value={s.value} onChange={(v) => update({ value: v })} dataSources={dataSources} scopeVars={scopeVars} placeholder="Numeric field per item" /></Field>
          <Field label="Colour">{renderColor(s.color, (color) => update({ color }))}</Field>
        </>
      )}
    />
  );
}

/* -------------------------------- key/values ------------------------------- */

export interface KeyValueItem {
  id: string;
  label: string;
  value: string;
}

/**
 * A row of a key/value list: a label and a value.
 *
 * The value used to be edited by **two** controls at once — a binding field that
 * wrapped what you picked in `{{ }}`, and a text box showing the same prop raw.
 * They were views of one field, so editing either silently rewrote what the other
 * displayed, and the raw one put `{{ sum(invoice.lineItems.amount) * invoice.taxRate }}`
 * in a text box for a user with no way to write one. It is one control and an
 * explicit choice now: a field, or some text.
 */
export function KeyValuesEditor({
  value,
  onChange,
  dataSources,
  scopeVars,
  allowExpressions = true
}: {
  value: KeyValueItem[];
  onChange: (v: KeyValueItem[]) => void;
  dataSources: DataSource[];
  scopeVars?: Record<string, FieldSchema>;
  allowExpressions?: boolean;
}) {
  const humanise = useTokenText(dataSources, scopeVars);
  const summarise = (text: string) => (allowExpressions ? text : humanise(text));
  return (
    <ListEditor<KeyValueItem>
      items={value}
      onChange={onChange}
      itemLabel="Item"
      // Humanised, because the collapsed row is where most people see a label and
      // "VAT at {{ invoice.taxRate | percent:0 }}" is the wrong half of it.
      summary={(k) => summarise(k.label) || "Item"}
      create={() => ({ id: createId("k"), label: "Label", value: "" })}
      renderItem={(k, update) => {
        const bound = /^\{\{[^}]*\}\}$/.test(k.value.trim());
        return (
          <>
            <Field label="Label">
              <TextOrTokens
                value={k.label}
                onChange={(label) => update({ label })}
                dataSources={dataSources}
                scopeVars={scopeVars}
                allowExpressions={allowExpressions}
              />
            </Field>
            <Field label="Shows" stacked>
              <Segmented
                value={bound ? "field" : "text"}
                ariaLabel="What this row shows"
                onChange={(mode) => update({ value: mode === "field" ? "" : k.value.replace(/\{\{|\}\}/g, "").trim() })}
                options={[
                  { value: "field", label: "A field" },
                  { value: "text", label: "Some text" }
                ]}
              />
            </Field>
            {bound ? (
              <Field label="Field" stacked>
                <BindingField
                  value={k.value.replace(/^\{\{\s*|\s*\}\}$/g, "")}
                  onChange={(v) => update({ value: v ? `{{ ${v} }}` : "" })}
                  dataSources={dataSources}
                  scopeVars={scopeVars}
                  placeholder="Choose a field"
                  allowExpression={allowExpressions}
                />
              </Field>
            ) : (
              <Field label="Text" stacked>
                <TextOrTokens
                  value={k.value}
                  onChange={(value) => update({ value })}
                  dataSources={dataSources}
                  scopeVars={scopeVars}
                  allowExpressions={allowExpressions}
                  placeholder="Type what this row should say"
                />
              </Field>
            )}
          </>
        );
      }}
    />
  );
}

/**
 * A line of text that may carry `{{ tokens }}`.
 *
 * The same treatment `FieldRenderer` gives a `text` prop, which the list editors
 * never got because they render their controls directly rather than through it: a
 * label reading `VAT at {{ invoice.taxRate | percent:0 }}` is template syntax in a
 * plain box, editable only into something broken by anybody without an expression
 * editor. Shown in field names and read-only there; an ordinary input everywhere
 * else, and everywhere the line has no tokens in it at all.
 */
function TextOrTokens({
  value,
  onChange,
  dataSources,
  scopeVars,
  allowExpressions,
  placeholder
}: {
  value: string;
  onChange: (v: string) => void;
  dataSources: DataSource[];
  scopeVars?: Record<string, FieldSchema>;
  allowExpressions: boolean;
  placeholder?: string;
}) {
  const humanise = useTokenText(dataSources, scopeVars);
  if (!allowExpressions && value.includes("{{")) {
    return <TextInput value={humanise(value)} onChange={() => undefined} readOnly title="This line fills itself in from your data." />;
  }
  return <TextInput value={value} onChange={onChange} lazy {...(placeholder ? { placeholder } : {})} />;
}

/* ------------------------------ generic sub-form ---------------------------- */

export function SubFormList({ value, onChange, fields, itemLabel, summary, renderField }: { value: Array<Record<string, unknown>>; onChange: (v: Array<Record<string, unknown>>) => void; fields: InspectorField[]; itemLabel?: string; summary?: (item: Record<string, unknown>, index: number) => string; renderField: (field: InspectorField, value: unknown, onChange: (v: unknown) => void) => ReactNode }) {
  return (
    <ListEditor<Record<string, unknown> & { id?: string }>
      items={value}
      onChange={onChange}
      itemLabel={itemLabel ?? "Item"}
      // The generic fallback reaches for whatever field looks like a name. That
      // is fine for a series and useless for a rule keyed by column id, which
      // is why a block can describe its own items.
      summary={(it, i) => summary?.(it, i) || String(it.label ?? it.header ?? `${itemLabel ?? "Item"} ${i + 1}`)}
      create={() => ({ id: createId("i") })}
      renderItem={(it, update) => <>{fields.map((f) => <div key={f.prop}>{renderField(f, it[f.prop], (v) => update({ [f.prop]: v }))}</div>)}</>}
    />
  );
}
